import {
  MicHandler,
  MicOptions,
  QueueSttStartResp,
  QueueSttEndResp,
  QueueSttSendResp,
  RecorderData,
  QueueSttSendTimeoutResp
} from "../../../models/microphone";
import { AbstMic } from "../common";
import {
  encodeRawData,
  concatenateFloat32Array,
  buildWavHeader
} from "../../audio";
import {
  MIC_ERR_LIFECYCLE,
  MIC_ERR_STT_QUEUE
} from "../../../constants/microphone";
import { ScriptNodeManager, getMediaStreamError } from "../input";
import { RawAudioManager } from "../input";
import RecordRTC from "recordrtc";

export type MicQueuingSttStatus = "none" | "start" | "send" | "end";

export type MicQueueingSttUrlBuilder = (
  s3name: string,
  queueId: string
) => string;

function concatenate(resultConstructor: any, ...arrays: any) {
  let totalLength = 0;
  for (const arr of arrays) {
    totalLength += arr.length;
  }
  const result = new resultConstructor(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}

function convertPcmToWav(buffer: any) {
  // create WAV header
  const sampleRate = 16000;
  const numChannels = 1;
  const [type, format] = [Uint8Array, 1];
  const wavHeader = new Uint8Array(
    buildWavHeader({
      numFrames: buffer.byteLength,
      bytesPerSample: 2,
      sampleRate: sampleRate,
      numChannels: numChannels,
      format: format
    })
  );

  // wavHeader + raw PCM Bytes
  const wavBytes = new Uint8Array(wavHeader.length + buffer.byteLength);
  wavBytes.set(wavHeader, 0);
  wavBytes.set(new Uint8Array(buffer), wavHeader.length);
  return wavBytes;
}

function mergeLeftRightBuffers(config: any, callback?: any) {
  function mergeBuffers(channelBuffer: any, rLength: any) {
    const result = new Float64Array(rLength);
    let offset = 0;
    const lng = channelBuffer.length;

    for (let i = 0; i < lng; i++) {
      const buffer = channelBuffer[i];
      result.set(buffer, offset);
      offset += buffer.length;
    }

    return result;
  }

  function linearInterpolate(before: any, after: any, atPoint: any) {
    return before + (after - before) * atPoint;
  }

  // for changing the sampling rate, reference:
  // http://stackoverflow.com/a/28977136/552182
  function interpolateArray(data: any, newSampleRate: any, oldSampleRate: any) {
    const fitCount = Math.round(data.length * (newSampleRate / oldSampleRate));
    //var newData = new Array();
    const newData = [];
    //var springFactor = new Number((data.length - 1) / (fitCount - 1));
    const springFactor = Number((data.length - 1) / (fitCount - 1));
    newData[0] = data[0]; // for new allocation
    for (let i = 1; i < fitCount - 1; i++) {
      const tmp = i * springFactor;
      //var before = new Number(Math.floor(tmp)).toFixed();
      //var after = new Number(Math.ceil(tmp)).toFixed();
      const before = Number(Number(Math.floor(tmp)).toFixed());
      const after = Number(Number(Math.ceil(tmp)).toFixed());
      const atPoint = tmp - before;
      newData[i] = linearInterpolate(data[before], data[after], atPoint);
    }
    newData[fitCount - 1] = data[data.length - 1]; // for new allocation
    return newData;
  }

  function mergeAudioBuffers(config: any, cb?: any) {
    const numberOfAudioChannels = config.numberOfAudioChannels;

    // todo: "slice(0)" --- is it causes loop? Should be removed?
    let leftBuffers = config.leftBuffers.slice(0);
    let rightBuffers = config.rightBuffers.slice(0);
    let sampleRate = config.sampleRate;
    const internalInterleavedLength = config.internalInterleavedLength;
    const desiredSampRate = 16000;

    if (numberOfAudioChannels === 2) {
      leftBuffers = mergeBuffers(leftBuffers, internalInterleavedLength);
      rightBuffers = mergeBuffers(rightBuffers, internalInterleavedLength);
      if (desiredSampRate) {
        leftBuffers = interpolateArray(
          leftBuffers,
          desiredSampRate,
          sampleRate
        );
        rightBuffers = interpolateArray(
          rightBuffers,
          desiredSampRate,
          sampleRate
        );
      }
    }

    if (numberOfAudioChannels === 1) {
      leftBuffers = mergeBuffers(leftBuffers, internalInterleavedLength);
      if (desiredSampRate) {
        leftBuffers = interpolateArray(
          leftBuffers,
          desiredSampRate,
          sampleRate
        );
      }
    }

    // set sample rate as desired sample rate
    if (desiredSampRate) {
      sampleRate = desiredSampRate;
    }

    function interleave(leftChannel: any, rightChannel: any) {
      const length = leftChannel.length + rightChannel.length;

      const result = new Float64Array(length);

      let inputIndex = 0;

      for (let index = 0; index < length; ) {
        result[index++] = leftChannel[inputIndex];
        result[index++] = rightChannel[inputIndex];
        inputIndex++;
      }
      return result;
    }

    function writeUTFBytes(view: any, offset: any, string: any) {
      const lng = string.length;
      for (let i = 0; i < lng; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    }

    // interleave both channels together
    let interleaved;

    if (numberOfAudioChannels === 2) {
      interleaved = interleave(leftBuffers, rightBuffers);
    }

    if (numberOfAudioChannels === 1) {
      interleaved = leftBuffers;
    }

    const interleavedLength = interleaved.length;

    // create wav file
    const resultingBufferLength = 44 + interleavedLength * 2;

    const buffer = new ArrayBuffer(resultingBufferLength);

    const view = new DataView(buffer);

    // RIFF chunk descriptor/identifier
    writeUTFBytes(view, 0, "RIFF");

    // RIFF chunk length
    view.setUint32(4, 44 + interleavedLength * 2, true);

    // RIFF type
    writeUTFBytes(view, 8, "WAVE");

    // format chunk identifier
    // FMT sub-chunk
    writeUTFBytes(view, 12, "fmt ");

    // format chunk length
    view.setUint32(16, 16, true);

    // sample format (raw)
    view.setUint16(20, 1, true);

    // stereo (2 channels)
    view.setUint16(22, numberOfAudioChannels, true);

    // sample rate
    view.setUint32(24, sampleRate, true);

    // byte rate (sample rate * block align)
    view.setUint32(28, sampleRate * 2, true);

    // block align (channel count * bytes per sample)
    view.setUint16(32, numberOfAudioChannels * 2, true);

    // bits per sample
    view.setUint16(34, 16, true);

    // data sub-chunk
    // data chunk identifier
    writeUTFBytes(view, 36, "data");

    // data chunk length
    view.setUint32(40, interleavedLength * 2, true);

    // write the PCM samples
    const lng = interleavedLength;
    let index = 44;
    const volume = 1;
    for (let i = 0; i < lng; i++) {
      view.setInt16(index, interleaved[i] * (0x7fff * volume), true);
      index += 2;
    }

    if (cb) {
      return cb({
        buffer: buffer,
        view: view
      });
    } else {
      return {
        buffer,
        view
      };
    }

    // postMessage({
    //   buffer: buffer,
    //   view: view
    // });
  }

  if (callback) {
    mergeAudioBuffers(config, function(data: any) {
      callback(data.buffer, data.view);
    });
  } else {
    return mergeAudioBuffers(config);
  }

  // const webWorker = processInWebWorker(mergeAudioBuffers);
  //
  // webWorker.onmessage = function(event) {
  //   callback(event.data.buffer, event.data.view);
  //
  //   // release memory
  //   URL.revokeObjectURL(webWorker.workerURL);
  // };
  //
  // webWorker.postMessage(config);
}

export interface MicQueuingSttOptions extends MicOptions {
  /**
   * Custom AudioContext object
   */
  audioCtx: AudioContext;
  /**
   * Period of audio recording before sending to Queue STT. In milliseconds
   */
  queueIntervalPeriod?: number;
  /**
   * Base URL for Queue STT API
   */
  queueSttBaseURL?: string;
  /**
   * S3 bucket name (no `s3://` prefix) for Queue STT API
   */
  queueSttS3Bucket?: string;
  /**
   * Audio URL builder
   */
  queueSttUrlBuilder?: MicQueueingSttUrlBuilder;
}

/**
 * Use the Queue STT system
 *
 * Documentation (VPN required):
 * http://joyz-internal-documentation.s3-website-ap-northeast-1.amazonaws.com/projects/private/stt-queue-proxy/stt-queue-proxy.html
 */
export class MicQueuingStt extends AbstMic implements MicHandler {
  // --------------------------------------------------------------------------
  //    Attributes
  // --------------------------------------------------------------------------
  private _audioContext: AudioContext;
  private _options: MicQueuingSttOptions;
  private _recorderManager?: ScriptNodeManager;
  private _rawAudioManager?: RawAudioManager;

  /**
   * Raw data taken from the ScriptNode. Only mono channel is recorded
   */
  private _totalBuffer: Float32Array = new Float32Array(0);
  /**
   * Raw data taken from iOS  mic
   */
  private _totalRawBuffer: Uint8Array = new Uint8Array(0);
  /**
   * Group data per "second" to send to the queue STT
   */
  private _queueBuffer: Float32Array[] = [new Float32Array(0)];
  private _rawQueueBuffer: Uint8Array[] = [new Uint8Array(0)];
  /**
   * The interval to regularly create a new item in the `queueBuffer`
   */
  private _queueInterval?: number;
  /**
   * The period of the `queueInterval` object, in milliseconds
   */
  private _queueIntervalPeriod: number;
  /**
   * The queueId sent by the STT proxy
   */
  private _queueId?: string;
  /**
   * Flag to prevent cross-API calls
   */
  private _sttIsProcessing = false;
  private _sttRawIsProcessing = false;
  /**
   * Flag to keep track of the STT processing in the `queueBuffer` array
   */
  private _sttProcessingIdx = 0;
  private _sttRawProcessingIdx = 0;
  /**
   * Track what kind of API call is currently active
   */
  private _sttStatus: MicQueuingSttStatus = "none";
  /**
   * Queuing STT Base URL
   */
  private _sttBaseURL: string;
  /**
   * Queuing STT S3 bucket
   */
  private _sttS3Bucket: string;
  /**
   * To generate the audio URL
   */
  private _sttUrlBuilder: MicQueueingSttUrlBuilder;
  /**
   * Is window.RawAudioRecorder available
   */
  private _rawAudioRecorderAvailable: boolean;

  private _recordRTCRecorder?: RecordRTC;

  private _internalRecorderCurrentLength?: number;

  readonly _isChrome: boolean;

  private _microphone?: MediaStream = undefined;

  // --------------------------------------------------------------------------
  //    Initialisation
  // --------------------------------------------------------------------------
  constructor(options: MicQueuingSttOptions) {
    super(options);

    this._options = options;

    this._sttBaseURL =
      options.queueSttBaseURL || "https://stt-queue-dev.joyzbackend.com";
    this._sttS3Bucket = options.queueSttS3Bucket || "queue-recordings-dev";
    this._sttUrlBuilder =
      options.queueSttUrlBuilder ||
      ((s3, qId) => `https://${s3}.static.joyzbackend.com/${qId}.wav`);

    this.log(`Init QueueSTT API: ${this._sttBaseURL}`);
    this.log(`Init QueueSTT S3: ${this._sttS3Bucket}`);

    const appVersion = navigator.appVersion;
    this._isChrome = appVersion.match(/chrome/i) !== null;

    // Customization
    this._queueIntervalPeriod = options.queueIntervalPeriod || 1000;

    this._rawAudioRecorderAvailable =
      (window as any).RawAudioRecorder !== undefined;

    if (this._rawAudioRecorderAvailable) {
      this._rawAudioManager = new RawAudioManager();
    } else {
      this._recorderManager = new ScriptNodeManager({
        log: msg => this.log(msg)
      });
    }

    this._audioContext = options.audioCtx;
  }

  async init() {
    this._status = "ready";
  }

  // --------------------------------------------------------------------------
  //    Getter
  // --------------------------------------------------------------------------
  /**
   * @returns {string} how many buffer item have been processed over the number of
   * total buffer items
   */
  get currSttProgress(): string {
    return `${this._sttProcessingIdx}/${this._queueBuffer.length - 1}`;
  }

  get currRawSttProgress(): string {
    return `${this._sttRawProcessingIdx}/${this._rawQueueBuffer.length - 1}`;
  }

  get sttStatus(): MicQueuingSttStatus {
    return this._sttStatus;
  }

  // --------------------------------------------------------------------------
  //    Methods: Microphone
  // --------------------------------------------------------------------------
  /**
   * Start recording and recognizing sending
   *
   * --- Error handling ---
   * > MediaStream error most likely is due to "permission denial".
   * The status is then switching to "init-err" so that the user cannot re-try
   * except from re-loading the page.
   * > For other errors, status is "ready" because it might be a non-reproductible
   * error and should not, then, prevent the user from retrying
   */
  async _captureMicrophone() {
    try {
      const microphone = await navigator.mediaDevices.getUserMedia({
        audio: true
      });

      this._recordRTCRecorder = new RecordRTC(microphone, {
        disableLogs: false,
        numberOfAudioChannels: 1,
        ondataavailable: blob => this.onNewRecordRTCData(blob),
        timeSlice: this._queueIntervalPeriod,
        recorderType: RecordRTC.StereoAudioRecorder,
        type: "audio"
      });

      this._internalRecorderCurrentLength = 0;
      this._microphone = microphone;
    } catch (error) {
      console.log("_captureMicrophone error:", error);
    }
  }

  async start(): Promise<void> {
    if (this._rawAudioRecorderAvailable) {
      await this.startWithRawAudioRecorder();
    } else if (this._isChrome) {
      await this.startWithRecordRTC();
    } else {
      await this.startWithRecorderManager();
    }
  }

  async startWithRecordRTC() {
    await this._resetRawValues();

    await this._captureMicrophone();

    this._recordRTCRecorder?.reset();

    this._recordRTCRecorder?.startRecording();

    this._onRecordingStarted();

    let queueIntervalPeriod = this._queueIntervalPeriod;
    if ((window as any).STT_QUEUE_INTERVAL_PERIOD) {
      queueIntervalPeriod = parseInt((window as any).STT_QUEUE_INTERVAL_PERIOD);

      // not using this.log(...) because this has to be printed all the times
      console.log(
        `%c[QueueSTT] Custom queueIntervalPeriod: ${queueIntervalPeriod}ms`,
        "color: #0be8bf;"
      );
    }

    // Start queuing stuff
    this._queueInterval = window.setInterval(() => {
      this._addNewRecordRCTQueueBufferItem();
    }, queueIntervalPeriod);

    this._status = "permission";
    this._status = "recording";
  }

  async startWithRecorderManager() {
    this.log("START");
    this._resetValues();

    try {
      this._status = "permission";
      await this._recorderManager!.start(ev => this.onNewData(ev));
      this.log("STARTED all connected");

      // Use global variable to override
      let queueIntervalPeriod = this._queueIntervalPeriod;
      if ((window as any).STT_QUEUE_INTERVAL_PERIOD) {
        queueIntervalPeriod = parseInt(
          (window as any).STT_QUEUE_INTERVAL_PERIOD
        );

        // not using this.log(...) because this has to be printed all the times
        console.log(
          `%c[QueueSTT] Custom queueIntervalPeriod: ${queueIntervalPeriod}ms`,
          "color: #0be8bf;"
        );
      }

      // Start queuing stuff
      this._queueInterval = window.setInterval(() => {
        this._addNewQueueBufferItem();
      }, queueIntervalPeriod);

      this._onRecordingStarted();
      this._timeStart = window.performance.now();
      this._status = "recording";
    } catch (err) {
      console.log({ err });
      const mediaStreamErr = getMediaStreamError(err);
      if (mediaStreamErr) {
        this.addError(mediaStreamErr);
        this._status = "init-err";
      } else {
        this.addUnknownError({
          location: "start() method fails",
          err,
          errAsStringify: JSON.stringify(err)
        });
        this._status = "ready";
      }
    }
  }

  rawAudioRecorderOnStart() {
    this._resetRawValues();
    this._status = "recording";

    let queueIntervalPeriod = this._queueIntervalPeriod;
    if ((window as any).STT_QUEUE_INTERVAL_PERIOD) {
      queueIntervalPeriod = parseInt((window as any).STT_QUEUE_INTERVAL_PERIOD);

      // not using this.log(...) because this has to be printed all the times
      console.log(
        `%c[QueueSTT] Custom queueIntervalPeriod: ${queueIntervalPeriod}ms`,
        "color: #0be8bf;"
      );
    }

    // Start queuing stuff
    this._queueInterval = window.setInterval(() => {
      this._addNewRawQueueBufferItem();
    }, queueIntervalPeriod);

    this._onRecordingStarted();
    this._timeStart = window.performance.now();
  }

  rawAudioRecorderOnNewData(newData: number[]) {
    this.onNewRawData(newData);
  }

  rawAudioRecorderOnStop() {
    if (this._sttRawProcessingIdx < this._rawQueueBuffer.length) {
      window.clearInterval(this._queueInterval);
      this.log(`STOP at ${this.currRawSttProgress}: Proceeding`);

      this._queueInterval = window.setInterval(() => {
        if (!this._sttRawIsProcessing) this.processRawContextQueue();
      }, this._queueIntervalPeriod);
    }
    // No more buffer item to process
    else {
      // "end" chunk not sent
      if (this._sttStatus !== "end") {
        this.submitLastRawAudio();
      }

      window.clearInterval(this._queueInterval);
      this.log(`STOP at ${this.currRawSttProgress}: all finished`);
    }

    this._status = "stopped";
  }

  rawAudioRecorderOnError(error: any) {
    console.log("raw audio recorder error:", error);
  }

  async startWithRawAudioRecorder() {
    const rawAudioRecorder = (window as any).RawAudioRecorder;

    rawAudioRecorder.startListening(
      () => this.rawAudioRecorderOnStart(),
      (newData: number[]) => this.rawAudioRecorderOnNewData(newData),
      () => this.rawAudioRecorderOnStop(),
      (error: any) => this.rawAudioRecorderOnError(error)
    );
  }

  /**
   * Stop recording and manage the remaining audio data sending to the queue STT
   */
  _releaseMicrophone() {
    const tracks = this._microphone?.getTracks();

    tracks?.forEach((track: any) => {
      track.stop();
    });

    (this._microphone as any)?.stop();
  }

  async stop(): Promise<void> {
    if (this._rawAudioRecorderAvailable) {
      await this.stopWithRawAudioRecorder();
    } else if (this._isChrome) {
      await this.stopWithRecordRTC();
    } else {
      await this.stopWithRecorderManager();
    }
  }

  async stopWithRecordRTC() {
    this._recordRTCRecorder?.stopRecording(() => this.processRecordRTCData());

    this._releaseMicrophone();

    this._isProcessing = true;

    if (this._sttRawProcessingIdx < this._rawQueueBuffer.length) {
      window.clearInterval(this._queueInterval);
      this.log(`STOP at ${this.currRawSttProgress}: Proceeding`);

      this._queueInterval = window.setInterval(() => {
        if (!this._sttRawIsProcessing) this.processRecordRTCContextQueue();
      }, this._queueIntervalPeriod);
    }
    // No more buffer item to process
    else {
      // "end" chunk not sent
      if (this._sttStatus !== "end") {
        await this.submitLastRecordRTCAudio();
      }

      window.clearInterval(this._queueInterval);
      this.log(`STOP at ${this.currRawSttProgress}: all finished`);
    }

    this._status = "stopped";
  }

  async stopWithRecorderManager() {
    if (this._status === "init-err") {
      throw new Error(`Cannot stop microphone with ${this._status} status`);
    }

    this._onRecordingStopped(this.audioData);
    this._timeEnd = window.performance.now();
    this._onRecognizingStarted();
    // Indicate that some task is on-going. Flag is reset to false when the
    // last chunk is successfully sent
    this._isProcessing = true;

    // Stop the recording
    try {
      await this._recorderManager!.stop();
    } catch (err) {
      console.warn("Error when stopping MicQueuingStt:", err);
      this.addUnknownError({ err, errAsStringify: JSON.stringify(err) });
    }

    // iOS Safari: detect if no recording has been detected
    if (this._totalBuffer.length === 0) {
      this.addError(MIC_ERR_STT_QUEUE.NO_DATA);
    }

    // There are some buffer items left
    if (this._sttProcessingIdx < this._queueBuffer.length) {
      window.clearInterval(this._queueInterval);
      this.log(`STOP at ${this.currSttProgress}: Proceeding`);

      this._queueInterval = window.setInterval(() => {
        if (!this._sttIsProcessing) this.processContextQueue();
      }, this._queueIntervalPeriod);
    }
    // No more buffer item to process
    else {
      // "end" chunk not sent
      if (this._sttStatus !== "end") {
        this.submitLastAudio();
      }

      window.clearInterval(this._queueInterval);
      this.log(`STOP at ${this.currSttProgress}: all finished`);
    }

    // this.buildAudio();
    this._status = "stopped";
  }

  async stopWithRawAudioRecorder() {
    const rawAudioRecorder = (window as any).RawAudioRecorder;

    this._isProcessing = true;
    rawAudioRecorder.stopListening();
  }

  /**
   * Force stop a in-progress processing
   */
  async onSilenceDetected(): Promise<void> {
    this.stop();
    this._onSilenceDetected();
    await this.cancel();
    this._resetValues();
    this._resetRawValues();
    this._status = "ready";
  }

  async cancel(): Promise<void> {
    window.clearInterval(this._queueInterval);
    this._onRecordingError("Cancelled");
    this._onRecognizingError("Cancelled");

    this._status = "ready";
    this._sttStatus = "none";
    this._isProcessing = false;
  }

  /**
   * Reset all reset-able values
   */
  private _resetValues() {
    // Reset status
    this._error = undefined;

    // Reset buffer
    this._totalBuffer = new Float32Array(0);

    // Reset queue handling
    this._queueId = undefined;
    this._queueBuffer = [new Float32Array(0)];
    window.clearInterval(this._queueInterval);

    // Reset STT info
    this._sttIsProcessing = false;
    this._sttProcessingIdx = 0;
    this._sttStatus = "none";
  }

  private _resetRawValues() {
    // Reset status
    this._error = undefined;

    // Reset buffer
    this._totalRawBuffer = new Uint8Array(0);

    // Reset queue handling
    this._queueId = undefined;
    this._rawQueueBuffer = [new Uint8Array(0)];
    window.clearInterval(this._queueInterval);

    // Reset STT info
    this._sttRawIsProcessing = false;
    this._sttRawProcessingIdx = 0;
    this._sttStatus = "none";

    this._internalRecorderCurrentLength = 0;
  }

  processRecordRTCData() {
    const internalRecorder: any = this._recordRTCRecorder?.getInternalRecorder();

    if (internalRecorder) {
      const leftChannel = internalRecorder.leftchannel;
      const rightChannel = internalRecorder.rightchannel;

      const { buffer } = mergeLeftRightBuffers({
        desiredSampRate: internalRecorder.desiredSampRate,
        sampleRate: internalRecorder.sampleRate,
        numberOfAudioChannels: internalRecorder.numberOfAudioChannels,
        internalInterleavedLength: internalRecorder.recordingLength,
        leftBuffers: leftChannel,
        rightBuffers:
          internalRecorder.numberOfAudioChannels === 1 ? [] : rightChannel
      });

      const blob = new Blob([buffer], {
        type: "audio/wav"
      });

      this._onRecordingStopped([blob]);
    }
  }
  /**
   * Add a new queue buffer item. Newly generated data will be now appended
   * to the newly created item. The previous item, if it exists, is now ready
   * to be sent to the Queue STT
   */
  private _addNewQueueBufferItem() {
    // Only append a new array when the last one is not empty
    if (this._queueBuffer[this._queueBuffer.length - 1].length > 0) {
      this._queueBuffer = [...this._queueBuffer, new Float32Array(0)];

      // If there is no current STT processing, attempt to process the previous one
      if (!this._sttIsProcessing) {
        // if (!this._sttIsProcessing && this._queueBuffer.length > 1) {
        this.processContextQueue();
      }
    }
  }

  private _addNewRawQueueBufferItem() {
    // Only append a new array when the last one is not empty
    if (this._rawQueueBuffer[this._rawQueueBuffer.length - 1].length > 0) {
      this._rawQueueBuffer = [...this._rawQueueBuffer, new Uint8Array(0)];

      // If there is no current STT processing, attempt to process the previous one
      if (!this._sttRawIsProcessing) {
        // if (!this._sttIsProcessing && this._queueBuffer.length > 1) {
        this.processRawContextQueue();
      }
    }
  }

  private _addNewRecordRCTQueueBufferItem() {
    // Only append a new array when the last one is not empty
    if (this._rawQueueBuffer[this._rawQueueBuffer.length - 1].length > 0) {
      this._rawQueueBuffer = [...this._rawQueueBuffer, new Uint8Array(0)];

      // If there is no current STT processing, attempt to process the previous one
      if (!this._sttRawIsProcessing) {
        // if (!this._sttIsProcessing && this._queueBuffer.length > 1) {
        this.processRecordRTCContextQueue();
      }
    }
  }

  // --------------------------------------------------------------------------
  //    Methods: ScriptProcessorNode
  // --------------------------------------------------------------------------
  onNewData(ev: AudioProcessingEvent) {
    // Mono channel only
    const newFrames = ev.inputBuffer.getChannelData(0);

    if (this._totalBuffer.length === 32000) {
      const silenceDetected = this._totalBuffer.every(value => {
        return value === 0;
      });

      if (silenceDetected) {
        this.onSilenceDetected();
        return;
      }
    }

    // Update the totalBuffer
    this._totalBuffer = concatenateFloat32Array(this._totalBuffer, newFrames);

    // Update the buffer for queueing STT
    const qBuffIdx = this._queueBuffer.length - 1;
    const qBuffItem = this._queueBuffer[qBuffIdx];
    this._queueBuffer[qBuffIdx] = concatenateFloat32Array(qBuffItem, newFrames);
  }

  onNewRawData(chunks: number[]) {
    const newFrames = Uint8Array.from(chunks);

    if (this._totalBuffer.length === 32000) {
      const silenceDetected = this._totalRawBuffer.every(value => {
        return value === 0;
      });

      if (silenceDetected) {
        this.onSilenceDetected();
        return;
      }
    }

    this._totalRawBuffer = concatenate(
      Uint8Array,
      this._totalRawBuffer,
      newFrames
    );

    // Update the buffer for queueing STT
    const qBuffIdx = this._rawQueueBuffer.length - 1;
    const qBuffItem = this._rawQueueBuffer[qBuffIdx];
    this._rawQueueBuffer[qBuffIdx] = concatenate(
      Uint8Array,
      qBuffItem,
      newFrames
    );
  }

  async onNewRecordRTCData(blob: Blob) {
    const buffer = await new Response(blob).arrayBuffer();

    const decodedData = await this._audioContext.decodeAudioData(buffer);

    const channelData = decodedData.getChannelData(0);

    const silenceDetected = channelData.every(value => {
      return value === 0;
    });

    if (silenceDetected) {
      this.onSilenceDetected();
      return;
    }

    const newData = encodeRawData(
      channelData,
      this._audioContext.sampleRate,
      16000,
      false
    );

    const newFrames = new Uint8Array(newData.buffer);

    const qBuffIdx = this._rawQueueBuffer.length - 1;
    const qBuffItem = this._rawQueueBuffer[qBuffIdx];
    this._rawQueueBuffer[qBuffIdx] = concatenate(
      Uint8Array,
      qBuffItem,
      newFrames
    );
  }

  onRawRecorderError(error: any) {
    console.log({ error });
  }

  buildAudio(): Blob {
    const sampleRate = this._recorderManager!.audioContextSampleRate;

    const wavData = encodeRawData(
      this._totalBuffer,
      sampleRate,
      this._azSampleRate,
      true
    );

    // Adding a type has no effect on blob.size
    const wavBlob = new Blob([wavData], { type: this._azAudioType });
    return wavBlob;
  }

  buildRawAudio(): Blob {
    const wavData = convertPcmToWav(this._totalRawBuffer);

    // Adding a type has no effect on blob.size
    const wavBlob = new Blob([wavData], { type: this._azAudioType });
    return wavBlob;
  }

  buildRecordRTCAudio(): Blob {
    const internalRecorder: any = this._recordRTCRecorder?.getInternalRecorder();
    const leftChannel = internalRecorder.leftchannel;
    const rightChannel = internalRecorder.rightchannel;

    const { buffer } = mergeLeftRightBuffers({
      desiredSampRate: internalRecorder.desiredSampRate,
      sampleRate: internalRecorder.sampleRate,
      numberOfAudioChannels: internalRecorder.numberOfAudioChannels,
      internalInterleavedLength: internalRecorder.recordingLength,
      leftBuffers: leftChannel,
      rightBuffers:
        internalRecorder.numberOfAudioChannels === 1 ? [] : rightChannel
    });

    return new Blob([buffer], { type: this._azAudioType });
  }

  // --------------------------------------------------------------------------
  //    Methods: STT
  // --------------------------------------------------------------------------
  /**
   * Log before and after the API call. If `resp` is empty, it is assumed that
   * log happens before the API call.
   *
   * @param chunk chunk to be sent to STT API
   * @param resp optional response from API
   */
  private _logApiCall(chunk: DataView, resp?: any) {
    const text = resp ? resp : `Start (sending ${chunk.byteLength} bytes)`;

    const log = `${this.currSttProgress} ${this._sttStatus}: ${text}`;
    this.log(log);
  }

  private _logRawApiCall(chunk: Uint8Array, resp?: any) {
    const text = resp ? resp : `Start (sending ${chunk.byteLength} bytes)`;

    const log = `${this.currRawSttProgress} ${this._sttStatus}: ${text}`;
    this.log(log);
  }

  /**
   * Resample a Float32Array before sending it to QueueSTT. Resample always
   * uses `this.__sttProcessingIdx`
   *
   * @returns a DataView with a proper sample rate
   */
  private _loadAudioChunk(): DataView | undefined {
    const sample = this._queueBuffer[this._sttProcessingIdx];

    if (!sample || sample.length === 0) {
      this.log(`Empty sample at ${this.currSttProgress}`);
      return undefined;
    }

    const sampleRate = this._recorderManager!.audioContextSampleRate;

    const dataView = encodeRawData(
      sample,
      sampleRate,
      this._azSampleRate,
      false
    );

    // const log = `Sample of ${sample.length} bits is resized to ${dataView.byteLength} bits at ${this._azSampleRate}Hz`;
    // this.log(log);

    return dataView;
  }

  private _loadRawAudioChunk(): Uint8Array | undefined {
    const sample = this._rawQueueBuffer[this._sttRawProcessingIdx];

    if (!sample || sample.length === 0) {
      this.log(`Empty sample at ${this.currSttProgress}`);
      return undefined;
    }

    // const byteArray = convertPcmToWav(sample);

    return sample;
  }
  /**
   * Helper method to provide headers for the various STT API calls
   *
   * @param chunk the chunk to send
   * @returns window.fetch parameters, with an optional queueId
   */
  private _fetchInfo(chunk: DataView): RequestInit {
    const headers: Record<string, string> = this._queueId
      ? { "Content-Type": "audio/wave", "queue-id": this._queueId }
      : { "Content-Type": "audio/wave" };

    return { method: "POST", headers, mode: "cors", body: chunk };
  }

  private _fetchRawInfo(chunk: Uint8Array): RequestInit {
    const headers: Record<string, string> = this._queueId
      ? { "Content-Type": "audio/wave", "queue-id": this._queueId }
      : { "Content-Type": "audio/wave" };

    return { method: "POST", headers, mode: "cors", body: chunk };
  }

  /**
   * When processing a queueBuffer item, dispatch the correct method
   */
  async processContextQueue() {
    // Nothing to do
    if (this._sttIsProcessing) {
      this.log(`Processing in progress, skipping at ${this._sttProcessingIdx}`);
      return;
    }

    // Check mRecQueue data
    if (
      this._sttProcessingIdx >= this._queueBuffer.length ||
      this._sttProcessingIdx < 0
    ) {
      this.log(`Invalid ctxProcessingIdx: ${this.currSttProgress}`);
      this.submitLastAudio();
      return;
    }

    // First chunk where queueId is not defined yet
    if (!this._queueId) {
      await this.submitFirstAudio();
    }
    // Last chunk is ... the last one in the queue when the ctx
    // has been torn down
    else if (
      this._status !== "recording" &&
      this._sttProcessingIdx === this._queueBuffer.length - 1
    ) {
      await this.submitLastAudio();
    }
    // anything else is a next chunk
    else {
      await this.submitNextAudio();
    }
  }

  async processRawContextQueue() {
    // Nothing to do
    if (this._sttRawIsProcessing) {
      this.log(
        `Processing in progress, skipping at ${this._sttRawProcessingIdx}`
      );
      return;
    }

    // Check mRecQueue data
    if (
      this._sttRawProcessingIdx >= this._rawQueueBuffer.length ||
      this._sttRawProcessingIdx < 0
    ) {
      this.log(`Invalid ctxProcessingIdx: ${this.currSttProgress}`);
      this.submitLastRawAudio();
      return;
    }

    // First chunk where queueId is not defined yet
    if (!this._queueId) {
      await this.submitFirstRawAudio();
    }
    // Last chunk is ... the last one in the queue when the ctx
    // has been torn down
    else if (
      this._status !== "recording" &&
      this._sttRawProcessingIdx === this._rawQueueBuffer.length - 1
    ) {
      await this.submitLastRawAudio();
    }
    // anything else is a next chunk
    else {
      await this.submitNextRawAudio();
    }
  }

  async processRecordRTCContextQueue() {
    // Nothing to do
    if (this._sttRawIsProcessing) {
      this.log(
        `Processing in progress, skipping at ${this._sttRawProcessingIdx}`
      );
      return;
    }

    // Check mRecQueue data
    if (
      this._sttRawProcessingIdx >= this._rawQueueBuffer.length ||
      this._sttRawProcessingIdx < 0
    ) {
      this.log(`Invalid ctxProcessingIdx: ${this.currSttProgress}`);
      this.submitLastRecordRTCAudio();
      return;
    }

    // First chunk where queueId is not defined yet
    if (!this._queueId) {
      await this.submitFirstRecordRTCAudio();
    }
    // Last chunk is ... the last one in the queue when the ctx
    // has been torn down
    else if (
      this._status !== "recording" &&
      this._sttRawProcessingIdx === this._rawQueueBuffer.length - 1
    ) {
      await this.submitLastRecordRTCAudio();
    }
    // anything else is a next chunk
    else {
      await this.submitNextRecordRTCAudio();
    }
  }

  async detectTimeoutOrProceed(sendResp: QueueSttSendResp) {
    const respAsTimeout = sendResp as QueueSttSendTimeoutResp;

    // no timetout
    if (!respAsTimeout.error) {
      return;
    }

    // timeout detected
    if (respAsTimeout.error.includes("does not exist")) {
      this.stop();
      return;
    }

    // unknown error
    this.addUnknownError({
      method: "detectTimeoutOrProceed",
      resp: sendResp,
      queueId: this._queueId
    });
  }

  /**
   * Go ahead!
   */
  async submitFirstAudio() {
    this._sttIsProcessing = true;

    const firstChunk = this._loadAudioChunk();
    if (!firstChunk) {
      this.cancel();
      return;
    }

    try {
      this._sttStatus = "start";

      this._logApiCall(firstChunk);
      const resp = await window.fetch(
        `${this._sttBaseURL}/start`,
        this._fetchInfo(firstChunk)
      );
      const respJson = (await resp.json()) as QueueSttStartResp;

      if (respJson["queue-id"]) {
        this._queueId = respJson["queue-id"];
        this._logApiCall(firstChunk, JSON.stringify(respJson));
        this._sttProcessingIdx++;
      } else {
        this._logApiCall(
          firstChunk,
          `No queue-id found in response ${JSON.stringify(respJson)}`
        );

        this.addError({
          ...MIC_ERR_STT_QUEUE.NO_QUEUE_ID_RECEIVED,
          details: { resp: respJson, queueId: this._queueId }
        });

        window.clearInterval(this._queueInterval);
        this._resetValues();
      }
    } catch (err) {
      this.addError({
        ...MIC_ERR_STT_QUEUE.API_CALL,
        details: { method: "submitFirstAudio", err, queueId: this._queueId }
      });
    } finally {
      this._sttIsProcessing = false;
    }
  }

  async submitFirstRawAudio() {
    this._sttRawIsProcessing = true;

    const firstChunk = this._loadRawAudioChunk();
    if (!firstChunk) {
      this.cancel();
      return;
    }

    try {
      this._sttStatus = "start";

      this._logRawApiCall(firstChunk);
      const resp = await window.fetch(
        `${this._sttBaseURL}/start`,
        this._fetchRawInfo(firstChunk)
      );
      const respJson = (await resp.json()) as QueueSttStartResp;

      if (respJson["queue-id"]) {
        this._queueId = respJson["queue-id"];
        this._logRawApiCall(firstChunk, JSON.stringify(respJson));
        this._sttRawProcessingIdx++;
      } else {
        this._logRawApiCall(
          firstChunk,
          `No queue-id found in response ${JSON.stringify(respJson)}`
        );

        this.addError({
          ...MIC_ERR_STT_QUEUE.NO_QUEUE_ID_RECEIVED,
          details: { resp: respJson, queueId: this._queueId }
        });

        window.clearInterval(this._queueInterval);
        this._resetRawValues();
      }
    } catch (err) {
      this.addError({
        ...MIC_ERR_STT_QUEUE.API_CALL,
        details: { method: "submitFirstAudio", err, queueId: this._queueId }
      });
    } finally {
      this._sttRawIsProcessing = false;
    }
  }

  async submitFirstRecordRTCAudio() {
    this._sttRawIsProcessing = true;

    const internalRecorder: any = this._recordRTCRecorder!.getInternalRecorder();
    const internalBlob = internalRecorder.blob;
    const firstQueueItem = this._rawQueueBuffer?.[0]?.length;

    if (!firstQueueItem && internalBlob) {
      await this.onNewRecordRTCData(internalBlob);
    }

    const firstChunk = this._loadRawAudioChunk();
    if (!firstChunk) {
      this.cancel();
      return;
    }

    try {
      this._sttStatus = "start";

      this._logRawApiCall(firstChunk);
      const resp = await window.fetch(
        `${this._sttBaseURL}/start`,
        this._fetchRawInfo(firstChunk)
      );
      const respJson = (await resp.json()) as QueueSttStartResp;

      if (respJson["queue-id"]) {
        this._queueId = respJson["queue-id"];
        this._logRawApiCall(firstChunk, JSON.stringify(respJson));
        this._sttRawProcessingIdx++;
      } else {
        this._logRawApiCall(
          firstChunk,
          `No queue-id found in response ${JSON.stringify(respJson)}`
        );

        this.addError({
          ...MIC_ERR_STT_QUEUE.NO_QUEUE_ID_RECEIVED,
          details: { resp: respJson, queueId: this._queueId }
        });

        window.clearInterval(this._queueInterval);
        this._resetRawValues();
      }
    } catch (err) {
      this.addError({
        ...MIC_ERR_STT_QUEUE.API_CALL,
        details: { method: "submitFirstAudio", err, queueId: this._queueId }
      });
    } finally {
      this._sttRawIsProcessing = false;
    }
  }

  async submitNextAudio() {
    this._sttIsProcessing = true;

    const chunk = this._loadAudioChunk();
    if (!chunk) {
      this.submitLastAudio();
      return;
    }

    try {
      this._sttStatus = "send";

      this._logApiCall(chunk);
      const resp = await window.fetch(
        `${this._sttBaseURL}/send`,
        this._fetchInfo(chunk)
      );
      const res = (await resp.json()) as QueueSttSendResp;
      this._logApiCall(chunk, JSON.stringify(res));

      this._sttProcessingIdx++;

      this.detectTimeoutOrProceed(res);
    } catch (err) {
      this.addError({
        ...MIC_ERR_STT_QUEUE.API_CALL,
        details: { method: "submitNextAudio", err, queueId: this._queueId }
      });
    } finally {
      this._sttIsProcessing = false;
    }
  }

  async submitNextRawAudio() {
    this._sttRawIsProcessing = true;

    const chunk = this._loadRawAudioChunk();
    if (!chunk) {
      this.submitLastRawAudio();
      return;
    }

    try {
      this._sttStatus = "send";

      this._logRawApiCall(chunk);
      const resp = await window.fetch(
        `${this._sttBaseURL}/send`,
        this._fetchRawInfo(chunk)
      );
      const res = (await resp.json()) as QueueSttSendResp;
      this._logRawApiCall(chunk, JSON.stringify(res));

      this._sttRawProcessingIdx++;

      this.detectTimeoutOrProceed(res);
    } catch (err) {
      this.addError({
        ...MIC_ERR_STT_QUEUE.API_CALL,
        details: { method: "submitNextAudio", err, queueId: this._queueId }
      });
    } finally {
      this._sttRawIsProcessing = false;
    }
  }

  async submitNextRecordRTCAudio() {
    this._sttRawIsProcessing = true;

    const chunk = this._loadRawAudioChunk();
    if (!chunk) {
      this.submitLastRecordRTCAudio();
      return;
    }

    try {
      this._sttStatus = "send";

      this._logRawApiCall(chunk);
      const resp = await window.fetch(
        `${this._sttBaseURL}/send`,
        this._fetchRawInfo(chunk)
      );
      const res = (await resp.json()) as QueueSttSendResp;
      this._logRawApiCall(chunk, JSON.stringify(res));

      this._sttRawProcessingIdx++;

      this.detectTimeoutOrProceed(res);
    } catch (err) {
      this.addError({
        ...MIC_ERR_STT_QUEUE.API_CALL,
        details: { method: "submitNextAudio", err, queueId: this._queueId }
      });
    } finally {
      this._sttRawIsProcessing = false;
    }
  }

  async submitLastAudio() {
    this._sttIsProcessing = true;

    const lastChunk = this._loadAudioChunk();

    if (!lastChunk) {
      // still recording, wait for next chunk
      if (this._status == "recording") {
        this.log(`Empty last chunk. Skipped as _status="recording"`);
        return;
      }
      // already ended
      else if (this._sttStatus == "end") {
        window.clearInterval(this._queueInterval);
        this.log(`Empty last chunk. Skipped and end as _sttStatus="end"`);
        return;
      }
    }

    let chunk = lastChunk || new DataView(new ArrayBuffer(0));
    if ((window as any).STT_QUEUE_MIN_END_LENGTH) {
      const minEndDuration = parseInt((window as any).STT_QUEUE_MIN_END_LENGTH);

      if (chunk.byteLength / 16384 < minEndDuration / 1000) {
        console.log(
          `%c[QueueSTT] End chunk of length ${chunk.byteLength}B omitted as shorted as ${minEndDuration}ms`,
          "color: #0be8bf;"
        );
        chunk = new DataView(new ArrayBuffer(0));
      }
    }

    try {
      this._sttStatus = "end";

      this._logApiCall(chunk);
      const resp = await window.fetch(
        `${this._sttBaseURL}/end`,
        this._fetchInfo(chunk)
      );
      const recogData = (await resp.json()) as QueueSttEndResp;
      this._logApiCall(chunk, JSON.stringify(recogData));

      if (this._queueId) {
        const wavBlob = this.buildAudio();
        const remoteUrl = this._sttUrlBuilder(this._sttS3Bucket, this._queueId);

        this.log(`END: remote URL=${remoteUrl}`);
        this.log(`END: wavBlob=${wavBlob}`);

        const recordData: RecorderData = { blob: wavBlob, remoteUrl };
        await this.addNewData(recordData, recogData);

        // if there is no valid data, switch back to "ready" state
        const hasValidRecording =
          this._micData.length > 0 &&
          this._micData.some(d => d.recognition.display);

        if (!hasValidRecording) {
          this._status = "ready";
        }

        this._onRecognizingStopped();
      } else {
        const err = `SubmitLastAudio: queueId undefined`;
        this.log(err);
        throw new Error(err);
      }
    } catch (err) {
      this.addError({
        ...MIC_ERR_STT_QUEUE.API_CALL,
        details: { method: "submitLastAudio", err, queueId: this._queueId }
      });

      this._onRecognizingError(err);
    } finally {
      // Clear processing
      this._isProcessing = false;
      this._sttIsProcessing = false;
      // Finish interval
      window.clearInterval(this._queueInterval);
    }
  }

  async submitLastRawAudio() {
    this._sttRawIsProcessing = true;

    const lastChunk = this._loadRawAudioChunk();

    if (!lastChunk) {
      // still recording, wait for next chunk
      if (this._status == "recording") {
        this.log(`Empty last chunk. Skipped as _status="recording"`);
        return;
      }
      // already ended
      else if (this._sttStatus == "end") {
        window.clearInterval(this._queueInterval);
        this.log(`Empty last chunk. Skipped and end as _sttStatus="end"`);
        return;
      }
    }

    let chunk = lastChunk || new Uint8Array();
    if ((window as any).STT_QUEUE_MIN_END_LENGTH) {
      const minEndDuration = parseInt((window as any).STT_QUEUE_MIN_END_LENGTH);

      if (chunk.byteLength / 16384 < minEndDuration / 1000) {
        console.log(
          `%c[QueueSTT] End chunk of length ${chunk.byteLength}B omitted as shorted as ${minEndDuration}ms`,
          "color: #0be8bf;"
        );
        chunk = new Uint8Array();
      }
    }

    try {
      this._sttStatus = "end";

      this._logRawApiCall(chunk);
      const resp = await window.fetch(
        `${this._sttBaseURL}/end`,
        this._fetchRawInfo(chunk)
      );
      const recogData = (await resp.json()) as QueueSttEndResp;
      this._logRawApiCall(chunk, JSON.stringify(recogData));

      if (this._queueId) {
        const wavBlob = this.buildRawAudio();

        const remoteUrl = this._sttUrlBuilder(this._sttS3Bucket, this._queueId);

        this.log(`END: remote URL=${remoteUrl}`);
        this.log(`END: wavBlob=${wavBlob}`);

        const recordData = { blob: wavBlob, remoteUrl };
        await this.addNewData(recordData, recogData);

        // if there is no valid data, switch back to "ready" state
        const hasValidRecording =
          this._micData.length > 0 &&
          this._micData.some(d => d.recognition.display);

        if (!hasValidRecording) {
          this._status = "ready";
        }

        this._onRecognizingStopped();
      } else {
        const err = `SubmitLastAudio: queueId undefined`;
        this.log(err);
        throw new Error(err);
      }
    } catch (err) {
      this.addError({
        ...MIC_ERR_STT_QUEUE.API_CALL,
        details: { method: "submitLastAudio", err, queueId: this._queueId }
      });

      this._onRecognizingError(err);
    } finally {
      // Clear processing
      this._isProcessing = false;
      this._sttRawIsProcessing = false;
      // Finish interval
      window.clearInterval(this._queueInterval);
    }
  }

  async submitLastRecordRTCAudio() {
    this._sttRawIsProcessing = true;

    const lastChunk = this._loadRawAudioChunk();

    if (!lastChunk) {
      // still recording, wait for next chunk
      if (this._status == "recording") {
        this.log(`Empty last chunk. Skipped as _status="recording"`);
        return;
      }
      // already ended
      else if (this._sttStatus == "end") {
        window.clearInterval(this._queueInterval);
        this.log(`Empty last chunk. Skipped and end as _sttStatus="end"`);
        return;
      }
    }

    let chunk = lastChunk || new Uint8Array();
    if ((window as any).STT_QUEUE_MIN_END_LENGTH) {
      const minEndDuration = parseInt((window as any).STT_QUEUE_MIN_END_LENGTH);

      if (chunk.byteLength / 16384 < minEndDuration / 1000) {
        console.log(
          `%c[QueueSTT] End chunk of length ${chunk.byteLength}B omitted as shorted as ${minEndDuration}ms`,
          "color: #0be8bf;"
        );
        chunk = new Uint8Array();
      }
    }

    try {
      this._sttStatus = "end";

      this._logRawApiCall(chunk);
      const resp = await window.fetch(
        `${this._sttBaseURL}/end`,
        this._fetchRawInfo(chunk)
      );
      const recogData = (await resp.json()) as QueueSttEndResp;
      this._logRawApiCall(chunk, JSON.stringify(recogData));

      if (this._queueId) {
        const wavBlob = this.buildRecordRTCAudio();

        const remoteUrl = this._sttUrlBuilder(this._sttS3Bucket, this._queueId);

        this.log(`END: remote URL=${remoteUrl}`);
        this.log(`END: wavBlob=${wavBlob}`);

        const recordData = { blob: wavBlob, remoteUrl };
        await this.addNewData(recordData, recogData);

        // if there is no valid data, switch back to "ready" state
        const hasValidRecording =
          this._micData.length > 0 &&
          this._micData.some(d => d.recognition.display);

        if (!hasValidRecording) {
          this._status = "ready";
        }

        this._onRecognizingStopped();
      } else {
        const err = `SubmitLastAudio: queueId undefined`;
        this.log(err);
        throw new Error(err);
      }
    } catch (err) {
      this.addError({
        ...MIC_ERR_STT_QUEUE.API_CALL,
        details: { method: "submitLastAudio", err, queueId: this._queueId }
      });

      this._onRecognizingError(err);
    } finally {
      // Clear processing
      this._isProcessing = false;
      this._sttRawIsProcessing = false;
      // Finish interval
      window.clearInterval(this._queueInterval);
    }
  }
}
